import { useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Button } from "@nosible/shared/components/ui/button";
import { Badge } from "@nosible/shared/components/ui/badge";
import { useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import {
  ArrowLeft,
  BarChart,
  Calendar,
  ChevronRight,
} from "lucide-react";
import { AccountPageHeader } from "@/account/components/AccountPageHeader";

type UsageEntry = {
  type: string;
  limit: number;
  used: number;
};

type SubscriptionPeriodUsage = {
  total: number;
  splits: UsageEntry[];
  timestamp: string;
};

export function SubPeriodsPage() {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { data: session } = useSession();

  const {
    data: subscription,
    isLoading: subLoading,
    error: subError,
  } = trpc.subscription.getSubscription.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId }
  );

  const {
    data: periods,
    isLoading: periodsLoading,
    error: periodsError,
  } = trpc.subscription.usage.pastUsage.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId }
  );

  useEffect(() => {
    if (!session) {
      navigate("/login");
    }
  }, [session, navigate]);

  if (subLoading || periodsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  const error = subError || periodsError;
  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error.message}</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!subscription || !periods) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Data not found</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const sortedPeriods = [...periods].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );

  const currentPeriod = sortedPeriods[0];
  const pastPeriods = sortedPeriods.slice(1);

  return (
    <div className="account-ui-page">
      <AccountPageHeader
        title="Usage Periods"
        description={`${subscription.plan?.name} plan usage across billing periods`}
        icon={BarChart}
        backTo={`/subscriptions/${subscriptionId}`}
        backLabel="Back to Subscription"
      />

      <div className="grid gap-6">
        {currentPeriod && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Current Period
                  </CardTitle>
                  <CardDescription>
                    {new Date(currentPeriod.startDate).toLocaleDateString()} -{" "}
                    {new Date(currentPeriod.endDate).toLocaleDateString()}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="default">Active</Badge>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/subscriptions/${subscriptionId}/period/${currentPeriod.id}`}>
                      View Details
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <UsageSummary usage={currentPeriod.usage} />
            </CardContent>
          </Card>
        )}

        {pastPeriods.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Past Periods</CardTitle>
              <CardDescription>
                Historical usage data from previous billing periods
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pastPeriods.map((period) => {
                    const usageEntries = period.usage?.splits ?? [];
                    const totalUsed = usageEntries.reduce(
                      (sum, u) => sum + u.used,
                      0
                    );
                    const totalLimit = usageEntries.reduce(
                      (sum, u) => sum + u.limit,
                      0
                    );
                    const percentage = totalLimit > 0 ? (totalUsed / totalLimit) * 100 : 0;

                    return (
                      <TableRow key={period.id}>
                        <TableCell className="font-medium text-silica">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-chromium-mist" />
                            <span>
                              {new Date(period.startDate).toLocaleDateString()} -{" "}
                              {new Date(period.endDate).toLocaleDateString()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-slate-core rounded-full overflow-hidden">
                              <div
                                className={`h-full ${percentage > 90
                                  ? "bg-red-500"
                                  : percentage > 70
                                    ? "bg-yellow-500"
                                    : "bg-neon-green"
                                  }`}
                                style={{ width: `${Math.min(percentage, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm text-chromium-mist">
                              {totalUsed.toLocaleString()} / {totalLimit.toLocaleString()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/subscriptions/${subscriptionId}/period/${period.id}`}>
                              View details
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {pastPeriods.length === 0 && !currentPeriod && (
          <Card>
            <CardContent className="py-8">
              <div className="text-center text-chromium-mist">
                <BarChart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No usage periods yet.</p>
                <p className="text-sm mt-1">
                  Usage history will appear here after your first billing period starts.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function UsageSummary({ usage }: { usage: unknown }) {
  const usageData = (usage as SubscriptionPeriodUsage) || { splits: [] };
  const usageEntries = usageData.splits || [];

  if (usageEntries.length === 0) {
    return (
      <div className="text-center py-4 text-chromium-mist">
        No usage data available for this period
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {usageEntries.map((usageEntry) => {
        const percentage =
          usageEntry.limit > 0 ? (usageEntry.used / usageEntry.limit) * 100 : 0;

        return (
          <div key={usageEntry.type} className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-silica capitalize">
                {usageEntry.type}
              </span>
              <span className="text-sm text-chromium-mist">
                {Math.round(percentage)}%
              </span>
            </div>
            <div className="h-2 bg-slate-core rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${percentage > 90
                  ? "bg-red-500"
                  : percentage > 70
                    ? "bg-yellow-500"
                    : "bg-neon-green"
                  }`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
            <div className="text-xs text-chromium-mist">
              {usageEntry.used.toLocaleString()} / {usageEntry.limit.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}
